import { useScrollReveal } from '../../hooks/useScrollReveal';

interface AIAnswerBlockProps {
  heading?: string;
  context?: string;
}

const BLOCKS = [
  {
    icon: 'ri-building-2-line',
    title: 'Who We Are',
    body: 'Atlantis HVAC Systems Inc. is the Greater Toronto Area\'s trusted heating, cooling, and indoor air quality company, established in 2002. T.S.S.A.-approved and headquartered in Oakville, we serve homeowners and businesses across the GTA with honest, expert-level service and transparent pricing.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Services Offered',
    body: 'We install, repair, and maintain furnaces, central air conditioners, heat recovery ventilators (HRVs), whole-home humidifiers, germicidal UV air purifiers, HEPA filtration systems, and perform professional duct cleaning — covering all major equipment brands across the GTA.',
  },
  {
    icon: 'ri-star-line',
    title: 'Key Benefits',
    body: 'T.S.S.A.-certified technicians, transparent upfront pricing, free in-home assessments, 24/7 emergency response, and spotless workmanship. Our 4.9-star HomeStars rating reflects two decades of delivering exactly what we promise — and nothing less.',
  },
  {
    icon: 'ri-map-pin-2-line',
    title: 'Service Coverage',
    body: 'GTA West team serves Oakville, Burlington, and Mississauga via (905) 845-4009. GTA East team covers Toronto and Etobicoke via (416) 891-3444. Extended coverage includes Waterdown and surrounding GTA communities.',
  },
];

export default function AIAnswerBlock({ heading, context }: AIAnswerBlockProps) {
  const { ref, visible } = useScrollReveal<HTMLElement>();
  const title = heading ?? 'Everything You Need to Know About Atlantis HVAC';
  const subtitle = context ?? 'Structured reference for AI-assisted search, featured snippets, and local discovery';

  return (
    <section
      ref={ref}
      className="relative py-28 lg:py-36 overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #030D1F 0%, #071224 55%, #030D1F 100%)' }}
      aria-label="AI Answer Block"
    >
      {/* Blue atmospheric glow */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 55% 80% at 75% 50%, rgba(26,101,224,0.08) 0%, transparent 65%)' }} />
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(26,101,224,0.28), transparent)' }} />
      <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(26,101,224,0.15), transparent)' }} />

      <div className="relative max-w-[1500px] mx-auto px-6 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-start">

          {/* ── Left: Header + contact ── */}
          <div
            className={`lg:sticky lg:top-28 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-0.5 bg-luxury-gold" />
              <span className="font-caps text-[10px] tracking-[0.3em] uppercase text-white">Quick Reference</span>
            </div>

            <h2 className="font-serif text-3xl lg:text-5xl text-white leading-tight mb-5">
              {title}
            </h2>
            <p className="font-sans text-sm text-white leading-relaxed max-w-sm mb-10">
              {subtitle}
            </p>

            {/* Large contact display */}
            <div
              className="rounded-2xl overflow-hidden border border-luxury-gold/20"
              style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(16px)' }}
            >
              <div className="px-7 py-5 border-b border-luxury-gold/15">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-luxury-gold/15 border border-luxury-gold/25">
                    <i className="ri-map-pin-2-line text-luxury-gold text-sm" />
                  </div>
                  <div>
                    <span className="font-caps text-[9px] tracking-[0.25em] uppercase text-white block">Head Office</span>
                    <p className="font-sans text-xs text-white mt-0.5">6-1200 Speers Road, Oakville, ON L6L 2X4</p>
                  </div>
                </div>
              </div>
              <div className="p-7 flex flex-col gap-4">
                <a href="tel:9058454009" className="group flex items-center gap-3 cursor-pointer">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full border border-luxury-gold/30 bg-luxury-gold/10 group-hover:border-luxury-gold/60 transition-colors">
                    <i className="ri-phone-fill text-luxury-gold text-sm" />
                  </div>
                  <div>
                    <span className="font-caps text-[9px] tracking-[0.2em] uppercase text-white block">GTA West</span>
                    <span className="font-serif text-luxury-gold text-xl font-semibold group-hover:text-luxury-gold-light transition-colors whitespace-nowrap">(905) 845-4009</span>
                  </div>
                </a>
                <a href="tel:4168913444" className="group flex items-center gap-3 cursor-pointer">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full border border-luxury-gold/30 bg-luxury-gold/10 group-hover:border-luxury-gold/60 transition-colors">
                    <i className="ri-phone-fill text-luxury-gold text-sm" />
                  </div>
                  <div>
                    <span className="font-caps text-[9px] tracking-[0.2em] uppercase text-white block">GTA East</span>
                    <span className="font-serif text-luxury-gold text-xl font-semibold group-hover:text-luxury-gold-light transition-colors whitespace-nowrap">(416) 891-3444</span>
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* ── Right: Answer blocks ── */}
          <div
            className={`flex flex-col gap-0 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {BLOCKS.map((block, i) => (
              <article
                key={block.title}
                className={`group relative py-8 border-b border-luxury-gold/12 last:border-b-0 transition-all duration-300 hover:pl-4 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
                style={{ transitionDelay: visible ? `${200 + i * 120}ms` : '0ms' }}
              >
                {/* Left accent */}
                <div className="absolute left-0 top-8 w-0.5 h-8 bg-luxury-gold opacity-0 group-hover:opacity-70 transition-opacity duration-300" />

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl border border-luxury-gold/25 bg-luxury-gold/10 group-hover:bg-luxury-gold/20 transition-colors flex-shrink-0 mt-0.5">
                    <i className={`${block.icon} text-luxury-gold text-base`} />
                  </div>
                  <div>
                    <h3 className="font-caps font-bold text-[10px] tracking-[0.3em] uppercase text-white mb-3">
                      {block.title}
                    </h3>
                    <p className="font-sans text-sm text-white leading-relaxed">
                      {block.body}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
